import React from 'react';

/**
 * Contains a render failure to one subtree instead of letting it reach the
 * App-level boundary (views/App/component.js), which swaps the ENTIRE app for
 * a fallback — a whole-screen answer to a problem that is usually one card.
 *
 * Two kinds of child need this, and they are the same problem seen twice:
 *
 *  - An unreliable component. swiper@6 under React 17 throws intermittently
 *    during cold mount before its internal refs settle; unwrapped, that took
 *    `AdviserHomePage` down on first paint, killed the webpack-dev-server
 *    process, and left an infinite re-mount loop with no way to navigate out.
 *  - A `React.lazy` child. `<Suspense>` catches the PENDING state, never a
 *    REJECTED import — so a chunk that 404s (a deploy rotating the hash under a
 *    Capacitor shell still holding a cached index.html, or plain lost
 *    connectivity) throws straight past it. A lazy child that the adviser has
 *    not even opened can therefore replace the page they are working on, which
 *    is why every one of them belongs behind this rather than behind a bare
 *    Suspense.
 *
 * Renders nothing on error unless a `fallback` is given: for both cases above
 * the honest result is that one piece is missing, not that the page is broken.
 */
export type ErrorBoundaryProps = {
  children?: React.ReactNode;
  /** Shown in place of the children once they have thrown. Nothing by default. */
  fallback?: React.ReactNode;
  /** Names the subtree in the dev-only console warning. */
  label?: string;
};

type ErrorBoundaryState = { hasError: boolean };

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ''}] caught:`, error?.message);
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}
